<template>
  <div class="rich-text-editor">
    <quill-editor
      ref="myQuillEditor"
      v-model="content"
      :options="editorOption"
      @blur="onEditorBlur($event)"
      @focus="onEditorFocus($event)"
      @ready="onEditorReady($event)"
      @change="onEditorChange($event)"
      @keydown.native="handleKeyDown"
    />
    <!-- <span class="content-length">字数：{{ txtLength }}</span> -->
    <!-- 字符计数和限制提示 -->
    <div class="editor-char-count">
      <span
        :class="{
          'char-limit-min': isCharLimitMin,
          'char-limit-warning': isCharLimitWarning,
          'char-limit-exceeded': isCharLimitExceeded,
        }"
      >
        {{ txtLength }}/100000
      </span>
      <span v-if="isCharLimitMin" class="min-char-tip">最少需要100字</span>
      <span v-if="isCharLimitExceeded" class="max-char-tip">超出字符限制</span>
    </div>
    <!-- 快捷提示 -->
    <div class="editor-tips">
      <span>💡 提示：</span>
      <span
        >点击工具栏中的
        <strong
          style="
            background: #04cbcd;
            color: white;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 12px;
          "
          >插入文物</strong
        >
        按钮可实现插入文物</span
      >
      <span style="margin-left: 15px"
        >📋
        支持从外部网站复制粘贴文章，会自动清理格式并上传图片（支持外链图片和base64图片）</span
      >
    </div>

    <!-- 粘贴处理状态提示 -->
    <div v-if="pasteProcessing" class="paste-processing">
      <i class="el-icon-loading"></i>
      <span>正在处理粘贴内容中的图片，请稍候...</span>
    </div>

    <!-- 图片上传对话框 -->
    <el-dialog
      title="插入图片"
      :visible.sync="imageDialogVisible"
      width="800px"
    >
      <el-upload
        class="upload-demo"
        :before-upload="beforeImageUpload"
        :show-file-list="false"
        accept="image/*"
      >
        <el-button size="small" type="primary">选择图片</el-button>
        <div slot="tip" class="el-upload__tip">
          只能上传jpg/png文件，且不超过2MB
        </div>
      </el-upload>
      <br /><br />
      <img :src="dialogImageSrc" width="250" />
      <el-input
        class="m-input count"
        maxlength="50"
        show-word-limit
        v-model="dialogImageDes"
        placeholder="图片描述 5-50个字符"
      ></el-input>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="appendImage">插入图片</el-button>
      </div>
    </el-dialog>

    <!-- 文物选择对话框 -->
    <el-dialog
      title="选择文物"
      :visible.sync="artifactDialogVisible"
      width="800px"
      @close="closeArtifactDialog"
    >
      <el-select
        v-model="selectCollectionIds"
        placeholder="请检索文物"
        filterable
        remote
        :remote-method="fetchCollection"
        @focus="fetchCollection('文')"
        style="width: 600px"
      >
        <el-option
          v-for="item in collectionList"
          :key="item.collectInfo.id"
          :label="item.collectInfo.title"
          :value="item.collectInfo.id"
          class="collection-option"
          @click.native="setSelectCollect(item)"
        >
          <div class="item">
            <img :src="item?.collectInfo?.image?.url" class="image" />
            <div class="info">
              <a
                class="name text-overflow-2"
                :title="item.collectInfo.title"
                :alt="item.collectInfo.title"
                >{{ item.collectInfo.title }}</a
              >
            </div>
          </div>
        </el-option>
      </el-select>
      <br /><br />
      <img :src="selectCollectionInfo.image" width="150" />
      <el-input
        class="m-input count"
        maxlength="50"
        show-word-limit
        v-model="collectionDes"
        placeholder="文物描述 5-50个字符"
      ></el-input>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="appendArtifact">插入文物</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/*eslint-disable*/
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import {
  getCollectList,
  getFileUploadToken,
  getFileSourceUrl,
} from '@/servies/index.js';
import {
  fileUpload,
  calculateMD5Binary,
  calculateMD5Base64,
  getFileBase64By64Bytes,
} from '@/utils/upload.js';

// 扩展 Quill 的图片格式以支持 data-file-id 属性
import Quill from 'quill';
const Image = Quill.import('formats/image');
class CustomImage extends Image {
  static create(value) {
    const node = super.create(value);

    if (typeof value === 'object') {
      // 从对象创建（程序性插入）
      node.setAttribute('src', value.src);
      if (value.fileId) {
        node.setAttribute('data-file-id', value.fileId);
      }
      if (value.width) {
        node.setAttribute('data-width', value.width);
      }
      if (value.height) {
        node.setAttribute('data-height', value.height);
      }
      if (value.artifactId) {
        node.setAttribute('data-artifact-id', value.artifactId);
      }
      if (value.artifactName) {
        node.setAttribute('data-artifact-name', value.artifactName);
      }

      // 根据是否有文物属性应用样式
      if (value.artifactId) {
        node.style.border = '2px solid #04CBCD';
        node.style.borderRadius = '4px';
        node.style.padding = '2px';
      }
    } else if (typeof value === 'string') {
      // 从HTML解析时，value 是 src 字符串
      node.setAttribute('src', value);
    }

    return node;
  }

  static value(node) {
    const result = {
      src: node.getAttribute('src'),
    };

    // 保持所有自定义属性
    if (node.hasAttribute('data-file-id')) {
      result.fileId = node.getAttribute('data-file-id');
    }
    if (node.hasAttribute('data-width')) {
      result.width = node.getAttribute('data-width');
    }
    if (node.hasAttribute('data-height')) {
      result.height = node.getAttribute('data-height');
    }
    if (node.hasAttribute('data-artifact-id')) {
      result.artifactId = node.getAttribute('data-artifact-id');
    }
    if (node.hasAttribute('data-artifact-name')) {
      result.artifactName = node.getAttribute('data-artifact-name');
    }

    return result;
  }

  // 重写 domNode 方法，确保在从HTML解析时保持属性
  static match(node) {
    return node.tagName === 'IMG';
  }

  // 确保在编辑器渲染时保持所有属性
  optimize(context) {
    super.optimize(context);

    // 检查是否有文物属性，如果有就应用文物样式
    const artifactId = this.domNode.getAttribute('data-artifact-id');
    if (artifactId && !this.domNode.style.border) {
      this.domNode.style.border = '2px solid #04CBCD';
      this.domNode.style.borderRadius = '4px';
      this.domNode.style.padding = '2px';
    }
  }
}
// 设置 blotName 和 tagName 确保正确覆盖默认 image
CustomImage.blotName = 'image';
CustomImage.tagName = 'img';
// 注册自定义格式
Quill.register(CustomImage, true);

// 移除 ArtifactImage，现在统一使用 CustomImage

const Delta = Quill.import('delta');

// 自定义文物介绍 Block Blot
const Block = Quill.import('blots/block');
class ArtifactDescBlock extends Block {
  static create(value) {
    let node = super.create();
    node.setAttribute('class', 'artifact-desc');
    node.setAttribute('data-artifact-desc', '1');
    return node;
  }
  static formats(node) {
    // 只在有 data-artifact-desc=1 时才返回格式，否则返回 undefined
    if (
      node.hasAttribute('data-artifact-desc') &&
      node.getAttribute('data-artifact-desc') === '1'
    ) {
      return { 'artifact-desc-block': true };
    }
    return undefined;
  }
  format(name, value) {
    if (name === 'artifact-desc-block' && !value) {
      this.domNode.removeAttribute('data-artifact-desc');
      this.domNode.removeAttribute('class');
    } else {
      super.format(name, value);
    }
  }
}
ArtifactDescBlock.blotName = 'artifact-desc-block';
ArtifactDescBlock.tagName = 'p';

// 自定义 em 标签的 Inline Blot
const Inline = Quill.import('blots/inline');
class ArtifactDescInline extends Inline {
  static create(value) {
    let node = super.create();
    node.setAttribute('class', 'artifact-desc');
    node.setAttribute('data-artifact-desc', '1');
    return node;
  }
  static formats(node) {
    if (
      node.hasAttribute('data-artifact-desc') &&
      node.getAttribute('data-artifact-desc') === '1'
    ) {
      return { 'artifact-desc-inline': true };
    }
    return undefined;
  }
  format(name, value) {
    if (name === 'artifact-desc-inline' && !value) {
      this.domNode.removeAttribute('data-artifact-desc');
      this.domNode.removeAttribute('class');
    } else {
      super.format(name, value);
    }
  }
}
ArtifactDescInline.blotName = 'artifact-desc-inline';
ArtifactDescInline.tagName = 'i';

Quill.register(ArtifactDescBlock, true);
Quill.register(ArtifactDescInline, true);

// 自定义图片描述 Inline Blot
class ImageDescInline extends Inline {
  static create(value) {
    let node = super.create();
    node.setAttribute('class', 'image-desc');
    node.setAttribute('data-image-desc', '1');
    //node.style.fontStyle = 'italic'; // 确保斜体样式
    return node;
  }
  static formats(node) {
    if (
      node.hasAttribute('data-image-desc') &&
      node.getAttribute('data-image-desc') === '1'
    ) {
      return { 'image-desc-inline': true };
    }
    return undefined;
  }
  format(name, value) {
    if (name === 'image-desc-inline' && !value) {
      this.domNode.removeAttribute('data-image-desc');
      this.domNode.removeAttribute('class');
      // this.domNode.style.fontStyle = '';
    } else {
      super.format(name, value);
    }
  }
}
ImageDescInline.blotName = 'image-desc-inline';
ImageDescInline.tagName = 'em';
Quill.register(ImageDescInline, true);

export default {
  name: 'RichTextEditor',
  components: {
    quillEditor,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '请输入内容...',
    },
  },
  data() {
    return {
      txtLength: 0,
      minCharCount: 100, // 最小字符数
      maxCharCount: 100000, // 最大字符数
      dialogImageSrc: '',
      dialogImageFileId: '',
      dialogImageDes: '',
      savedSelection: null,
      selectCollectionIds: [],
      selectCollectionInfo: {},
      collectionList: [],
      collectionDes: '',

      content: this.value,
      imageDialogVisible: false,
      artifactDialogVisible: false,
      artifactSearchWord: '',
      artifactList: [],
      artifactLoading: false,
      uploadUrl: '', // 这里根据你的上传接口配置
      pasteProcessing: false, // 粘贴处理状态
      editorOption: {
        modules: {
          toolbar: {
            container: [
              // ['bold', 'italic', 'underline', 'strike'],
              // ['blockquote'],
              //   [{ header: 1 }, { header: 2 }],
              //   [{ list: 'ordered' }, { list: 'bullet' }],
              //   [{ script: 'sub' }, { script: 'super' }],
              //   [{ indent: '-1' }, { indent: '+1' }],
              //   [{ direction: 'rtl' }],
              //   [{ size: ['small', false, 'large', 'huge'] }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              //   [{ color: [] }, { background: [] }],
              //   [{ font: [] }],
              // [{ align: [] }],
              ['clean'],
              ['image'], // 只保留标准的图片按钮
            ],
            handlers: {
              image: this.insertImage,
            },
          },
          clipboard: {
            // 匹配视觉样式，但我们会自定义处理
            matchVisual: false,
            // 注意：matchers 会在 mounted 中动态添加，避免 this 引用问题
          },
        },
        placeholder: this.placeholder,
        theme: 'snow',
        // 允许的格式，限制粘贴内容只能使用这些格式
        formats: [
          'header',
          'bold',
          'italic',
          'underline',
          'image',
          'link',
          'list',
          'bullet',
          'indent',
          'artifact-desc-inline',
          'image-desc-inline',
          'artifact-image',
        ],
      },
    };
  },
  computed: {
    isCharLimitMin() {
      return this.txtLength < this.minCharCount;
    },
    isCharLimitWarning() {
      return this.txtLength > 95000 && this.txtLength <= this.maxCharCount;
    },
    isCharLimitExceeded() {
      return this.txtLength > this.maxCharCount;
    },
  },
  watch: {
    value(newVal) {
      this.content = newVal;
      // 当 value 发生变化时，需要直接设置 Quill 编辑器的内容
      this.$nextTick(() => {
        if (
          this.$refs.myQuillEditor &&
          this.$refs.myQuillEditor.quill &&
          newVal !== this.content
        ) {
          const quill = this.$refs.myQuillEditor.quill;
          // 使用 dangerouslyPasteHTML 设置 HTML 内容
          quill.root.innerHTML = newVal || '';
          // 重新计算字符数
          const text = quill.getText();
          const pureTextLength = text.replace(/\s/g, '').length;
          this.txtLength = pureTextLength;
        }
      });
    },
    content(newVal) {
      this.$emit('input', newVal);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initCustomButtons();
      this.setEditorHeight();
      this.overrideEnterForArtifactDesc();
    });
  },
  methods: {
    // 设置编辑器高度
    setEditorHeight() {
      const container = this.$el.querySelector('.ql-container');
      const editor = this.$el.querySelector('.ql-editor');

      if (container) {
        container.style.height = '500px';
      }
      if (editor) {
        editor.style.height = '470px';
        editor.style.overflowY = 'auto';
      }
    },
    // 初始化自定义按钮
    initCustomButtons() {
      const quill = this.$refs.myQuillEditor.quill;

      // 添加自定义文物按钮到工具栏
      const toolbar = quill.getModule('toolbar');

      // 创建文物按钮
      const artifactButton = document.createElement('button');
      artifactButton.innerHTML = '<s class="icon"></s>';
      artifactButton.className = 'ql-artifact';
      artifactButton.type = 'button';

      // 添加点击事件
      artifactButton.addEventListener('click', () => {
        this.insertArtifact();
      });

      // 找到图片按钮，在其后插入文物按钮
      const toolbarContainer = document.querySelector('.ql-toolbar');
      const imageButton = document.querySelector('.ql-image');

      if (toolbarContainer && imageButton) {
        // 在图片按钮后面插入文物按钮
        imageButton.parentNode.insertBefore(
          artifactButton,
          imageButton.nextSibling
        );
      } else if (toolbarContainer) {
        // 如果找不到图片按钮，就添加到工具栏末尾
        toolbarContainer.appendChild(artifactButton);
      }

      // 也可以通过工具栏的 handlers 设置
      toolbar.addHandler('artifact', this.insertArtifact);
    },
    // 编辑器事件
    onEditorBlur() {
      const quill = this.$refs.myQuillEditor.quill;
      this.savedRange = quill.getSelection();
    },
    onEditorFocus() {
      this.savedRange = null;
    },
    onEditorReady(quill) {
      console.log('editor ready!', quill);

      // 初始化粘贴处理器
      this.initAdvancedPasteHandler();

      // 编辑器就绪后，如果有初始内容，设置它
      if (this.value) {
        this.$nextTick(() => {
          quill.root.innerHTML = this.value;
          // 重新计算字符数
          const text = quill.getText();
          const pureTextLength = text.replace(/\s/g, '').length;
          this.txtLength = pureTextLength;
        });
      }
    },
    onEditorChange({ quill, html, text }) {
      // console.log('editor change!', quill, html, text);

      // 计算纯文本字符数（剔除所有空格）
      const pureTextLength = text.replace(/\s/g, '').length;

      // 更新字符数显示
      this.txtLength = pureTextLength;

      // 检查是否超出最大限制
      if (pureTextLength > this.maxCharCount) {
        // 超出限制，显示警告但不阻止输入（让用户自己决定是否删除）
        this.$message.warning(`内容不能超过${this.maxCharCount}个字符`);
      }

      this.content = html;
      this.$emit('change', {
        text: this.$refs.myQuillEditor.quill.getText(),
        html: html,
        length: this.txtLength,
        charCount: pureTextLength,
        isValid:
          pureTextLength >= this.minCharCount &&
          pureTextLength <= this.maxCharCount,
      });
      console.log('text>>>>>>' + text);
      console.log('text>>>>>>' + text.length);
    },
    // 插入图片
    insertImage() {
      // 方式1：直接打开文件选择器（推荐）
      // this.openFileSelector();

      const quill = this.$refs.myQuillEditor.quill;

      // 立即保存当前光标位置
      this.savedSelection = quill.getSelection();

      // 如果没有光标位置，设置到文档末尾
      if (!this.savedSelection) {
        const length = quill.getLength();
        this.savedSelection = { index: length > 0 ? length - 1 : 0, length: 0 };
        quill.setSelection(this.savedSelection);
      }

      // 方式2：如果需要显示对话框，取消注释下面这行
      this.imageDialogVisible = true;
    },
    openFileSelector() {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.style.display = 'none';

      input.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (file) {
          this.beforeImageUpload(file);
        }
      });
      input.click();
    },
    // 图片上传前的验证
    beforeImageUpload(file) {
      const isImage = file.type.startsWith('image/');
      const isLt2M = file.size / 1024 / 1024 < 2;

      if (!isImage) {
        this.$message.error('只能上传图片文件!');
        return false;
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!');
        return false;
      }

      // 使用你现有的上传逻辑
      this.uploadImageFile(file);
      return false; // 阻止默认上传
    },
    // 上传图片文件
    async uploadImageFile(file) {
      try {
        const base64 = await getFileBase64By64Bytes(file);
        const fileExtension = file.type.split('/')[1];
        const binaryHash = calculateMD5Binary(file);

        const tokenRes = await getFileUploadToken({
          platType: 3,
          fileHeadBase64: base64,
          fileSize: file.size,
          fileSubfix: fileExtension,
          fileContentMd5: calculateMD5Base64(binaryHash),
        });

        if (tokenRes.data.code === 200) {
          const token = tokenRes.data.data;
          const fileID = token.keyInfo.idstr;
          fileUpload(
            token,
            file,
            (percent) => {
              console.log(`图片上传进度: ${percent}%`);
            },
            (success) => {
              console.log('图片上传成功:', success);
              this.dialogImageSrc = success.Location;
              this.dialogImageFileId = fileID;
              //this.imageDialogVisible = false;
            },
            (error) => {
              console.error('图片上传失败:', error);
              this.$message.error('图片上传失败');
            }
          );
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        this.$message.error('图片上传失败');
      }
    },

    // 图片上传成功回调
    handleImageSuccess(response) {
      if (response.url) {
        this.insertImageToEditor(response.url);
        this.imageDialogVisible = false;
      }
    },

    // 插入图片到编辑器
    insertImageToEditor(imageUrl, fileID) {
      const quill = this.$refs.myQuillEditor.quill;
      const range = quill.getSelection();
      const imgObj = new window.Image();
      imgObj.src = imageUrl;
      imgObj.onload = () => {
        if (range) {
          // 插入图片
          quill.insertEmbed(range.index, 'image', {
            src: imageUrl,
            fileId: fileID,
            width: imgObj.width || 100,
            height: imgObj.height || 100,
          });
          // 在图片后插入一个换行，防止光标卡在图片前
          quill.insertText(range.index + 1, '\n');
          // 将光标定位到图片后方新行
          const newIndex = range.index + 2;
          quill.setSelection(newIndex, 0);

          // 清除所有格式，确保后续文本不会继承样式
          quill.format('bold', false);
          quill.format('italic', false);
          quill.format('color', false);
          quill.format('background', false);
          quill.format('image-desc-inline', false);
        }
      };

      //   debugger;
      //   if (range) {
      //     // 方法2：直接插入带有 fileID 的 HTML
      //     const imageHtml = `<img src="${imageUrl}" data-file-id="${fileID}" alt="uploaded image">`;
      //     quill.clipboard.dangerouslyPasteHTML(range.index, imageHtml);
      //   }
    },

    // 插入文物按钮
    insertArtifact() {
      const quill = this.$refs.myQuillEditor.quill;

      // 立即保存当前光标位置
      this.savedSelection = quill.getSelection();

      // 如果没有光标位置，设置到文档末尾
      if (!this.savedSelection) {
        const length = quill.getLength();
        this.savedSelection = { index: length > 0 ? length - 1 : 0, length: 0 };
        quill.setSelection(this.savedSelection);
      }

      this.artifactDialogVisible = true;
    },
    appendImage() {
      const quill = this.$refs.myQuillEditor.quill;
      const Delta = quill.constructor.import('delta');
      const imgObj = new window.Image();
      imgObj.src = this.dialogImageSrc;
      const des = this.dialogImageDes || '';
      let insertIndex = 0;

      if (
        this.savedSelection &&
        typeof this.savedSelection.index === 'number'
      ) {
        insertIndex = this.savedSelection.index;
      } else {
        const currentSelection = quill.getSelection();
        insertIndex = currentSelection
          ? currentSelection.index
          : quill.getLength();
      }

      imgObj.onload = () => {
        if (des) {
          let delta = new Delta()
            .retain(insertIndex)
            .insert({
              image: {
                src: this.dialogImageSrc,
                fileId: this.dialogImageFileId,
                width: imgObj.width || 100,
                height: imgObj.height || 100,
              },
            })
            .insert(des, { 'image-desc-inline': true })
            .insert('\n') // 结束图片+描述段落
            .insert('\n'); // 新增一个空段落

          quill.updateContents(delta, 'user');

          // 将光标定位到新空行
          const newIndex = insertIndex + 2 + des.length;
          quill.setSelection(newIndex, 0);

          // 清除所有格式
          quill.format('bold', false);
          quill.format('italic', false);
          quill.format('color', false);
          quill.format('background', false);
          quill.format('image-desc-inline', false);
        } else {
          // 只有图片，没有描述
          let delta = new Delta()
            .retain(insertIndex)
            .insert({
              image: {
                src: this.dialogImageSrc,
                fileId: this.dialogImageFileId,
                width: imgObj.width || 100,
                height: imgObj.height || 100,
              },
            })
            .insert('\n')
            .insert('\n'); // 新增一个空段落

          quill.updateContents(delta, 'user');

          // 将光标定位到新空行
          const newIndex = insertIndex + 2;
          quill.setSelection(newIndex, 0);

          quill.format('bold', false);
          quill.format('italic', false);
          quill.format('color', false);
          quill.format('background', false);
        }
        this.closeImageDialog();
      };
    },
    closeImageDialog() {
      this.dialogImageSrc = '';
      this.dialogImageFileId = '';
      this.dialogImageDes = '';
      this.imageDialogVisible = false;
    },
    // 选择文物后插入文物
    appendArtifact() {
      if (this.selectCollectionIds.length === 0) {
        this.$message.error('请选择文物');
        return;
      }

      const quill = this.$refs.myQuillEditor?.quill;
      let insertIndex = 0;

      if (
        this.savedSelection &&
        typeof this.savedSelection.index === 'number'
      ) {
        insertIndex = this.savedSelection.index;
      } else {
        const currentSelection = quill.getSelection();
        insertIndex = currentSelection
          ? currentSelection.index
          : quill.getLength();
      }

      // 使用图片方式插入文物
      const artifactId = this.selectCollectionInfo.id;
      const artifactName = this.selectCollectionInfo.name;
      const artifactImageUrl = this.selectCollectionInfo.image;
      const des = this.collectionDes || '';

      if (des) {
        let delta = new Delta()
          .retain(insertIndex)
          .insert({
            image: {
              src: artifactImageUrl,
              artifactId: artifactId,
              artifactName: artifactName,
            },
          })
          .insert(des, { 'artifact-desc-inline': true })
          .insert('\n') // 结束文物+描述段落
          .insert('\n'); // 新增一个空段落

        quill.updateContents(delta, 'user');

        // 将光标定位到新空行
        const newIndex = insertIndex + 2 + des.length;
        quill.setSelection(newIndex, 0);

        // 清除所有格式
        quill.format('bold', false);
        quill.format('italic', false);
        quill.format('color', false);
        quill.format('background', false);
        quill.format('artifact-desc-inline', false);
      } else {
        // 只有文物图片，没有描述
        let delta = new Delta()
          .retain(insertIndex)
          .insert({
            image: {
              src: artifactImageUrl,
              artifactId: artifactId,
              artifactName: artifactName,
            },
          })
          .insert('\n')
          .insert('\n'); // 新增一个空段落

        quill.updateContents(delta, 'user');

        // 将光标定位到新空行
        const newIndex = insertIndex + 2;
        quill.setSelection(newIndex, 0);

        quill.format('bold', false);
        quill.format('italic', false);
        quill.format('color', false);
        quill.format('background', false);
      }
      this.closeArtifactDialog();
    },
    closeArtifactDialog() {
      this.selectCollectionIds = [];
      this.selectCollectionInfo = {};
      this.collectionList = [];
      this.collectionDes = '';
      this.artifactDialogVisible = false;
    },
    setSelectCollect(item) {
      this.selectCollectionInfo = {
        id: item.collectInfo.id,
        name: item.collectInfo.title,
        image: item.collectInfo.image.url,
      };
    },

    // 搜索文物
    searchArtifacts(keyword) {
      this.artifactLoading = true;
      getCollectList({
        q: this.artifactSearchWord || keyword,
        did: this.$utils.getSTKUUID(),
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.artifactList = response.data.data.list || [];
          } else {
            this.$message.error('获取文物列表失败');
          }
          this.artifactLoading = false;
        })
        .catch((error) => {
          console.error('获取文物列表失败:', error);
          this.$message.error('获取文物列表失败');
          this.artifactLoading = false;
        });
    },

    fetchCollection(query) {
      getCollectList({ q: query, did: this.$utils.getSTKUUID() }).then(
        (res) => {
          let response = res.data;
          if (
            response.code === 200 &&
            response.data.contents &&
            response.data.contents.length > 0
          ) {
            this.collectionList = response.data.contents;
          }
        }
      );
    },

    // 处理键盘事件
    handleKeyDown(event) {
      if (
        event.key === 'A' &&
        (event.ctrlKey || event.metaKey) &&
        event.shiftKey
      ) {
        event.preventDefault();
        this.insertArtifact();
      }
    },

    // 获取文本长度
    getTextLength() {
      if (this.$refs.myQuillEditor && this.$refs.myQuillEditor.quill) {
        const quill = this.$refs.myQuillEditor.quill;
        return quill.getLength() - 1; // -1 因为 Quill 会在末尾添加一个换行符
      }
      return 0;
    },

    // ===== 粘贴处理方法 =====

    // 下载外部图片并上传到我们的服务器
    async downloadAndUploadImage(imageUrl) {
      console.log('🔄 开始下载外部图片:', imageUrl);

      // 策略1: 直接请求（支持CORS的图片）
      try {
        console.log('📡 尝试策略1: 直接CORS请求');
        const response = await fetch(imageUrl, {
          mode: 'cors',
          method: 'GET',
          headers: {
            Accept: 'image/*,*/*;q=0.8',
            'User-Agent':
              'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
            Referer: window.location.href,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          console.log('✅ 策略1成功:', blob.type, blob.size);

          if (blob.type.startsWith('image/')) {
            const file = new File(
              [blob],
              `external-image-${Date.now()}.${
                blob.type.split('/')[1] || 'jpg'
              }`,
              { type: blob.type }
            );
            return await this.uploadImageToServer(file);
          }
        }
      } catch (error) {
        console.log('❌ 策略1失败:', error.message);
      }

      // 策略3: Canvas方案（适用于同源或支持CORS的图片）
      try {
        console.log('📡 尝试策略3: Canvas转换');
        return await this.canvasImageDownload(imageUrl);
      } catch (error) {
        console.log('❌ 策略3失败:', error.message);
      }

      // 策略4: 尝试不同的请求模式
      try {
        console.log('📡 尝试策略4: no-cors模式');
        const response = await fetch(imageUrl, {
          mode: 'no-cors',
          method: 'GET',
        });

        // no-cors模式下无法读取响应内容，这个策略主要是为了触发浏览器缓存
        console.log('策略4: 已触发请求，尝试从缓存读取');

        // 立即尝试Canvas方案
        return await this.canvasImageDownload(imageUrl);
      } catch (error) {
        console.log('❌ 策略4失败:', error.message);
      }

      throw new Error('所有下载策略都失败了');
    },

    // 上传图片到服务器
    async uploadImageToServer(file) {
      try {
        let uploadFile = file;
        let fileExtension = 'png'; // 默认扩展名

        // 如果是 Blob 对象（来自 base64），转换为 File 对象
        if (file instanceof Blob && !(file instanceof File)) {
          const mimeType = file.type || 'image/png';
          fileExtension = mimeType.split('/')[1] || 'png';
          const fileName = `base64-image-${Date.now()}.${fileExtension}`;
          uploadFile = new File([file], fileName, { type: mimeType });
        } else if (file instanceof File) {
          fileExtension =
            file.type.split('/')[1] || file.name.split('.').pop() || 'png';
        }

        const base64 = await getFileBase64By64Bytes(uploadFile);
        const binaryHash = calculateMD5Binary(uploadFile);

        const tokenRes = await getFileUploadToken({
          platType: 3,
          fileHeadBase64: base64,
          fileSize: uploadFile.size,
          fileSubfix: fileExtension,
          fileContentMd5: calculateMD5Base64(binaryHash),
        });

        if (tokenRes.data.code === 200) {
          const token = tokenRes.data.data;
          const fileID = token.keyInfo.idstr;

          return new Promise((resolve, reject) => {
            fileUpload(
              token,
              uploadFile,
              (percent) => {
                console.log(`图片上传进度: ${percent}%`);
              },
              (success) => {
                console.log('图片上传成功:', success);
                resolve({
                  url: success.Location,
                  fileId: fileID,
                });
              },
              (error) => {
                console.error('图片上传失败:', error);
                reject(error);
              }
            );
          });
        } else {
          throw new Error('获取上传 token 失败');
        }
      } catch (error) {
        console.error('上传图片失败:', error);
        throw error;
      }
    },

    // 处理 base64 图片
    async processBase64Image(base64Data) {
      try {
        // 提取文件类型和数据
        const matches = base64Data.match(/^data:(.+);base64,(.+)$/);
        if (!matches || matches.length < 3) {
          throw new Error('无效的 base64 图片数据');
        }
        const mimeType = matches[1];
        const base64ImageData = matches[2];

        // 创建一个 Blob 对象
        const byteCharacters = atob(base64ImageData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: mimeType });

        // 使用现有的上传逻辑
        const uploadResult = await this.uploadImageToServer(blob);
        return uploadResult;
      } catch (error) {
        console.error('处理 base64 图片失败:', error);
        throw error;
      }
    },

    // ===== 粘贴处理方法结束 =====

    // 初始化高级粘贴处理器
    initAdvancedPasteHandler() {
      const quill = this.$refs.myQuillEditor?.quill;

      if (!quill) {
        console.error('Quill 编辑器未初始化，无法添加粘贴处理器');
        return;
      }

      console.log('初始化粘贴处理器', quill);

      // 保存 this 引用
      const self = this;

      // 首先处理 H 标签，确保优先级最高
      ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].forEach((tag) => {
        quill.clipboard.addMatcher(tag, (node, delta) => {
          console.log(
            `🏷️ 处理 ${tag.toUpperCase()} 标签:`,
            node.textContent?.substring(0, 50)
          );

          const text = node.textContent || '';
          if (text.trim()) {
            // 直接返回一个包含标题格式的 Delta
            const Delta = Quill.import('delta');
            const headerDelta = new Delta()
              .insert(text)
              .insert('\n', { header: 4 });

            console.log(
              `✅ ${tag.toUpperCase()} → H4 成功:`,
              text.substring(0, 30)
            );

            return headerDelta;
          } else {
            console.log(`⚠️ ${tag.toUpperCase()} 标签为空`);
            return new Delta();
          }
        });
      });

      // 样式清理处理器
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        // 只移除样式属性，不改变其他逻辑
        if (node && node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName?.toLowerCase();

          // 跳过H标签和图片标签的样式清理
          if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'img'].includes(tagName)) {
            return delta;
          }

          // 移除格式标签（strong、b、em、i、u）
          if (['strong', 'b', 'em', 'i', 'u'].includes(tagName)) {
            console.log(`🧹 移除 ${tagName.toUpperCase()} 格式标签`);

            // 只保留文本内容，不保留格式
            const text = node.textContent || '';
            if (text.trim()) {
              const Delta = Quill.import('delta');
              return new Delta().insert(text);
            } else {
              return new Delta();
            }
          }

          try {
            // 移除常见的样式属性
            ['style', 'class', 'id'].forEach((attr) => {
              if (node.hasAttribute && node.hasAttribute(attr)) {
                node.removeAttribute(attr);
                console.log(`🧹 移除 ${tagName} 的 ${attr} 属性`);
              }
            });
          } catch (e) {
            // 忽略错误，继续处理
          }
        }
        return delta;
      });

      // 添加真正的粘贴事件监听器，在这里处理图片
      const editorElement = quill.root;
      editorElement.addEventListener('paste', (e) => {
        console.log('🎯 粘贴事件触发!');

        const clipboardData = e.clipboardData || window.clipboardData;

        if (clipboardData && clipboardData.types.includes('text/html')) {
          const htmlData = clipboardData.getData('text/html');
          console.log('🏷️ HTML内容长度:', htmlData.length);
          console.log('🏷️ HTML内容预览:', htmlData.substring(0, 200000000));

          // 检查是否有图片需要处理
          const hasImages = htmlData.includes('<img');
          if (hasImages) {
            console.log('🖼️ 检测到图片，将进行异步处理');

            // 使用DOMParser准确分析图片类型
            try {
              const parser = new DOMParser();
              const doc = parser.parseFromString(htmlData, 'text/html');
              const imgElements = doc.querySelectorAll('img');

              let base64Count = 0;
              let networkCount = 0;

              imgElements.forEach((img) => {
                const src = img.getAttribute('src');
                if (src) {
                  if (src.startsWith('data:image/')) {
                    base64Count++;
                  } else if (
                    src.startsWith('http') ||
                    src.startsWith('https')
                  ) {
                    networkCount++;
                  }
                }
              });

              console.log(
                `📊 HTML解析结果: 总图片${imgElements.length}张, base64图片${base64Count}张, 外部图片${networkCount}张`
              );

              if (base64Count > 0) {
                console.log(`📊 将处理 ${base64Count} 张base64图片`);
                self.$message.info(`正在处理 ${base64Count} 张本地图片...`);
              }

              if (networkCount > 0) {
                console.log(`🌐 将处理 ${networkCount} 张网络图片`);
                self.$message.info(`正在下载 ${networkCount} 张网络图片...`);
              }

              // 只有确实需要处理图片时才设置处理状态
              if (base64Count > 0 || networkCount > 0) {
                self.pasteProcessing = true;

                // 延迟处理图片，让粘贴先完成
                setTimeout(() => {
                  self.processPastedImages();
                }, 100);
              }
            } catch (error) {
              console.error('❌ HTML解析失败:', error);
              // 降级到原来的逻辑
              self.pasteProcessing = true;
              setTimeout(() => {
                self.processPastedImages();
              }, 100);
            }
          }

          // 检查是否有样式
          // if (htmlData.includes('style=') || htmlData.includes('class=')) {
          //   console.log('🎨 检测到样式，将被清理');
          //   self.$message.info('正在清理样式...', 1000);
          // }

          console.log('✅ 允许默认处理');
        }

        // 延迟移除格式标签，确保在Quill处理完粘贴后执行
        setTimeout(() => {
          self.removeFormattingTags();
        }, 500);

        // 延迟处理图片描述合并
        setTimeout(() => {
          self.mergeImageDescriptions();
        }, 3000);
      });

      console.log('✅ 粘贴处理器初始化完成');
    },

    // 处理粘贴后的图片
    processPastedImages() {
      console.log('🔄 开始处理粘贴后的图片');

      const quill = this.$refs.myQuillEditor?.quill;
      if (!quill) return;

      // 查找编辑器中所有的图片元素
      const images = quill.root.querySelectorAll('img');
      console.log(`🔍 找到 ${images.length} 张图片`);

      // 过滤出需要处理的图片（外部链接或base64）
      const imagesToProcess = [];

      images.forEach((img, index) => {
        const src = img.getAttribute('src');
        if (!src) return;

        console.log(`🖼️ 检查第 ${index + 1} 张图片:`, src.substring(0, 100));

        // 只处理需要上传的图片（外部链接或base64），跳过已经处理过的本地图片
        if (src.startsWith('data:image/')) {
          console.log('📊 发现需要处理的base64图片');
          imagesToProcess.push({ img, src, type: 'base64' });
        } else if (src.startsWith('http') || src.startsWith('https')) {
          console.log('🌐 发现需要处理的外部网络图片');
          imagesToProcess.push({ img, src, type: 'external' });
        } else {
          console.log('🏠 本地图片或已处理图片，跳过');
        }
      });

      console.log(`📋 实际需要处理的图片数量: ${imagesToProcess.length}`);

      if (imagesToProcess.length === 0) {
        console.log('✅ 没有需要处理的图片');
        this.pasteProcessing = false;
        return;
      }

      // 处理需要上传的图片
      imagesToProcess.forEach(({ img, src, type }, index) => {
        console.log(
          `🔄 开始处理第 ${index + 1}/${imagesToProcess.length} 张图片`
        );

        if (type === 'base64') {
          this.handlePastedBase64Image(img, src);
        } else if (type === 'external') {
          this.handlePastedExternalImage(img, src);
        }
      });
    },

    // 合并图片描述
    mergeImageDescriptions() {
      console.log('🔗 开始合并图片描述');

      const quill = this.$refs.myQuillEditor?.quill;
      if (!quill) {
        console.log('❌ Quill编辑器未找到');
        return;
      }

      // 先处理嵌套在em标签中的图片
      this.extractImagesFromEm();

      let hasChanges = false;

      // 重复执行直到没有更多可合并的图片描述
      while (true) {
        const contents = quill.getContents();
        const ops = contents.ops;

        console.log('📊 当前编辑器内容操作数量:', ops.length);

        let foundMerge = false;

        for (let i = 0; i < ops.length - 1; i++) {
          const currentOp = ops[i];
          const nextOp = ops[i + 1];

          // 检查当前操作是否是图片
          if (
            currentOp.insert &&
            typeof currentOp.insert === 'object' &&
            currentOp.insert.image
          ) {
            console.log(
              '📷 找到图片操作:',
              currentOp.insert.image.src?.substring(0, 50)
            );

            // 检查下一个操作是否是换行
            if (
              nextOp &&
              nextOp.insert &&
              typeof nextOp.insert === 'string' &&
              nextOp.insert.trim() === ''
            ) {
              console.log('⏎ 图片后有换行');

              // 检查再下一个操作是否是文本段落
              const afterNextOp = ops[i + 2];
              const afterAfterNextOp = ops[i + 3];

              console.log('🔍 检查后续操作:', {
                afterNextText: afterNextOp?.insert?.substring?.(0, 50),
                hasItalic: afterNextOp?.attributes?.italic,
                nextIsNewline: afterAfterNextOp?.insert?.includes?.('\n'),
              });

              if (
                afterNextOp &&
                typeof afterNextOp.insert === 'string' &&
                afterNextOp.insert.trim() &&
                afterNextOp.attributes &&
                afterNextOp.attributes.italic === true &&
                afterAfterNextOp &&
                afterAfterNextOp.insert &&
                afterAfterNextOp.insert.includes('\n')
              ) {
                console.log('✅ 发现可合并的图片描述:', afterNextOp.insert);

                // 使用Delta操作重构这一部分内容
                const Delta = Quill.import('delta');

                // 计算到图片位置的偏移
                let positionBeforeImage = 0;
                for (let j = 0; j < i; j++) {
                  if (ops[j].insert && typeof ops[j].insert === 'string') {
                    positionBeforeImage += ops[j].insert.length;
                  } else if (
                    ops[j].insert &&
                    typeof ops[j].insert === 'object'
                  ) {
                    positionBeforeImage += 1;
                  }
                }

                console.log('📍 图片前的位置:', positionBeforeImage);

                // 删除图片后的换行、原描述文本和后续的部分换行
                const deleteStart = positionBeforeImage + 1; // 图片后的位置
                const deleteLength =
                  nextOp.insert.length + afterNextOp.insert.length + 1; // 换行+描述+部分换行

                console.log(
                  '🗑️ 删除位置:',
                  deleteStart,
                  '删除长度:',
                  deleteLength
                );
                quill.deleteText(deleteStart, deleteLength);

                // 在图片后直接插入描述
                console.log('📝 插入描述:', afterNextOp.insert);
                quill.insertText(positionBeforeImage + 1, afterNextOp.insert, {
                  'image-desc-inline': true,
                });

                // 在描述后插入换行
                quill.insertText(
                  positionBeforeImage + 1 + afterNextOp.insert.length,
                  '\n'
                );

                console.log('✅ 一个图片描述合并完成');
                foundMerge = true;
                hasChanges = true;
                break; // 跳出当前循环，重新获取ops
              }
            }
          }
        }

        // 如果这一轮没有找到可合并的，就退出循环
        if (!foundMerge) {
          break;
        }
      }

      if (hasChanges) {
        console.log('🎉 所有图片描述合并完成');
      } else {
        console.log('⚠️ 没有找到可合并的图片描述');
      }
    },

    // 处理粘贴后的 base64 图片
    async handlePastedBase64Image(imgElement, src) {
      const imageId = `img_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const placeholder = `[base64图片上传中-${imageId}]`;

      // 用占位符替换图片
      const textNode = document.createTextNode(placeholder);
      imgElement.parentNode.replaceChild(textNode, imgElement);

      try {
        console.log('🔄 [图片处理] 开始处理base64图片');
        const imageData = await this.processBase64Image(src);
        await this.replaceImagePlaceholderById(imageId, imageData, {
          width: imgElement.width || 300,
          height: imgElement.height || 200,
        });
      } catch (error) {
        console.error('❌ [图片处理] base64图片处理失败:', error);
        this.$message.error('base64图片上传失败');
        this.replaceImagePlaceholderById(imageId, null);
      }
    },

    // 处理粘贴后的外部图片
    async handlePastedExternalImage(imgElement, src) {
      const imageId = `img_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const placeholder = `[网络图片上传中-${imageId}]`;

      // 用占位符替换图片
      const textNode = document.createTextNode(placeholder);
      imgElement.parentNode.replaceChild(textNode, imgElement);

      try {
        console.log('🔄 [图片处理] 开始处理外部图片:', src);
        const imageData = await this.downloadAndUploadImage(src);
        await this.replaceImagePlaceholderById(imageId, imageData, {
          width: imgElement.width || 300,
          height: imgElement.height || 200,
        });
      } catch (error) {
        console.error('❌ [图片处理] 外部图片处理失败:', error);
        this.$message.error('外部图片下载失败');
        this.replaceImagePlaceholderById(imageId, null);
      }
    },

    overrideEnterForArtifactDesc() {
      const quill = this.$refs.myQuillEditor.quill;
      const Keyboard = Quill.import('modules/keyboard');
      // 全局回车清除所有样式
      quill.keyboard.addBinding(
        { key: 13 }, // 13 = Enter
        {
          handler: function (range, context) {
            // 插入换行
            quill.insertText(range.index, '\n', {});
            // 切换新行为正文（header: false，清除所有inline格式）
            quill.formatLine(range.index + 1, 1, { header: false });
            quill.formatText(range.index + 1, 1, {}); // 清除inline格式
            quill.setSelection(range.index + 1, 0);
            return false; // 阻止默认行为
          },
        }
      );
    },

    // 根据ID替换图片占位符（更精确的方法）
    async replaceImagePlaceholderById(imageId, imageData, imgNode) {
      const quill = this.$refs.myQuillEditor.quill;
      if (!quill) return;

      // 智能检测占位符类型
      const text = quill.getText();
      let placeholder = '';

      if (text.includes(`[base64图片上传中-${imageId}]`)) {
        placeholder = `[base64图片上传中-${imageId}]`;
      } else if (text.includes(`[网络图片上传中-${imageId}]`)) {
        placeholder = `[网络图片上传中-${imageId}]`;
      } else {
        console.warn('⚠️ 未找到对应的占位符:', imageId);
        return;
      }

      console.log(`🔍 查找占位符: ${placeholder}`);

      // 获取文档内容
      const contents = quill.getContents();
      let currentIndex = 0;
      let found = false;

      // 遍历文档内容查找占位符
      for (let i = 0; i < contents.ops.length; i++) {
        const op = contents.ops[i];

        if (op.insert && typeof op.insert === 'string') {
          const insertText = op.insert;
          const placeholderStart = insertText.indexOf(placeholder);

          if (placeholderStart !== -1) {
            const actualIndex = currentIndex + placeholderStart;
            console.log(`✅ 找到占位符位置: ${actualIndex}`);

            // 删除占位符
            quill.deleteText(actualIndex, placeholder.length);

            if (imageData) {
              // 插入真实图片
              quill.insertEmbed(actualIndex, 'image', {
                src: imageData.url,
                fileId: imageData.fileId,
                width: imgNode?.width || 300,
                height: imgNode?.height || 200,
              });

              console.log('✅ 图片替换成功:', imageData.url);
              // this.$message.success('图片上传成功', 1000);
            } else {
              // 处理失败，插入错误提示
              quill.insertText(actualIndex, '[图片上传失败]');
              console.log('❌ 图片处理失败');
            }

            found = true;
            break;
          }

          currentIndex += insertText.length;
        } else if (op.insert && typeof op.insert === 'object') {
          // 处理嵌入对象（如图片）
          currentIndex += 1;
        }
      }

      if (!found) {
        console.warn('⚠️ 未找到占位符:', placeholder);
      }

      // 检查是否还有其他占位符正在处理
      this.checkImageProcessingComplete();
    },

    // 替换图片占位符（保留原方法作为备用）
    replaceImagePlaceholder(placeholder, imageData, imgNode) {
      const quill = this.$refs.myQuillEditor.quill;
      if (!quill) return;

      // 查找占位符文本
      const text = quill.getText();
      const placeholderIndex = text.indexOf(placeholder);

      if (placeholderIndex !== -1) {
        // 删除占位符
        quill.deleteText(placeholderIndex, placeholder.length);

        if (imageData) {
          // 插入真实图片
          quill.insertEmbed(placeholderIndex, 'image', {
            src: imageData.url,
            fileId: imageData.fileId,
            width: imgNode?.width || 300,
            height: imgNode?.height || 200,
          });

          console.log('✅ 图片替换成功:', imageData.url);
          this.$message.success('图片上传成功', 1000);
        } else {
          // 处理失败，插入错误提示
          quill.insertText(placeholderIndex, '[图片上传失败]');
          console.log('❌ 图片处理失败');
        }

        // 检查是否还有其他占位符正在处理
        this.checkImageProcessingComplete();
      }
    },

    // 检查图片处理是否全部完成
    checkImageProcessingComplete() {
      this.$nextTick(() => {
        const quill = this.$refs.myQuillEditor.quill;
        if (!quill) return;

        const text = quill.getText();
        // 检查是否还有带ID的占位符
        const hasPlaceholders =
          text.includes('[base64图片上传中-') ||
          text.includes('[网络图片上传中-');

        if (!hasPlaceholders && this.pasteProcessing) {
          // 所有图片处理完成
          this.pasteProcessing = false;
          console.log('🎉 所有图片处理完成');

          // 统计成功和失败的图片
          const failedCount = (text.match(/\[图片上传失败\]/g) || []).length;
          const currentHtml = quill.root.innerHTML;
          const successCount = (currentHtml.match(/<img/g) || []).length;

          if (failedCount > 0) {
            this.$message.warning(
              `图片处理完成：${successCount} 张成功，${failedCount} 张失败`
            );
          } else if (successCount > 0) {
            this.$message.success(
              `图片处理完成：${successCount} 张图片上传成功`
            );
          }
        }
      });
    },

    // Canvas图片下载方案
    async canvasImageDownload(imageUrl) {
      console.log('🎨 Canvas方案开始:', imageUrl);

      return new Promise((resolve, reject) => {
        // 创建一个隐藏的图片元素来加载图片
        const img = new Image();

        // 设置跨域属性
        img.crossOrigin = 'anonymous';

        img.onload = async () => {
          try {
            console.log('🎨 图片加载成功，开始Canvas转换');

            // 创建 canvas 来转换图片
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            canvas.width = img.width;
            canvas.height = img.height;

            // 绘制图片到 canvas
            ctx.drawImage(img, 0, 0);

            // 转换为 blob
            canvas.toBlob(
              async (blob) => {
                if (blob) {
                  console.log('✅ Canvas转换成功:', blob.type, blob.size);

                  const file = new File(
                    [blob],
                    `canvas-image-${Date.now()}.png`,
                    {
                      type: 'image/png',
                    }
                  );

                  try {
                    const result = await this.uploadImageToServer(file);
                    resolve(result);
                  } catch (error) {
                    reject(error);
                  }
                } else {
                  reject(new Error('Canvas转换失败'));
                }
              },
              'image/png',
              0.9
            );
          } catch (error) {
            console.error('❌ Canvas处理失败:', error);
            reject(error);
          }
        };

        img.onerror = (error) => {
          console.error('❌ 图片加载失败:', error);
          reject(new Error('图片加载失败'));
        };

        // 尝试多种加载方式
        const tryLoad = (url, withCredentials = false) => {
          console.log(`🔄 尝试加载图片: ${url.substring(0, 50)}...`);

          if (withCredentials) {
            // 有些图片需要凭据
            img.crossOrigin = 'use-credentials';
          }

          // 添加时间戳避免缓存问题
          const timestamp = Date.now();
          const separator = url.includes('?') ? '&' : '?';
          img.src = `${url}${separator}t=${timestamp}&nocache=${Math.random()}`;
        };

        // 首先尝试匿名加载
        tryLoad(imageUrl);

        // 如果5秒内没有成功，尝试带凭据的方式
        setTimeout(() => {
          if (!img.complete) {
            console.log('🔄 尝试使用凭据加载');
            tryLoad(imageUrl, true);
          }
        }, 5000);
      });
    },

    // 移除格式标签
    removeFormattingTags() {
      console.log('�� 开始移除格式标签');

      const quill = this.$refs.myQuillEditor.quill;
      if (!quill) {
        console.log('❌ Quill编辑器未找到');
        return;
      }

      // 直接操作编辑器的DOM
      const editorDOM = quill.root;

      // 查找所有格式标签
      const formatTags = ['strong', 'b', 'i', 'u'];

      formatTags.forEach((tagName) => {
        const elements = editorDOM.querySelectorAll(tagName);
        console.log(
          `🔍 找到 ${elements.length} 个 ${tagName.toUpperCase()} 标签`
        );

        // 倒序处理，避免DOM变化影响NodeList
        for (let i = elements.length - 1; i >= 0; i--) {
          const element = elements[i];
          const textContent = element.textContent;
          console.log(
            `📝 移除 ${tagName.toUpperCase()} 标签，保留文本: "${textContent}"`
          );

          // 创建文本节点替换格式标签
          const textNode = document.createTextNode(textContent);
          element.parentNode.replaceChild(textNode, element);
        }
      });

      console.log('✅ 格式标签移除完成');
    },

    // 提取嵌套标签中的图片，统一结构为 <p><img><描述></p>
    extractImagesFromEm() {
      console.log('🔧 开始提取嵌套标签中的图片');

      const quill = this.$refs.myQuillEditor?.quill;
      if (!quill) return;

      const editorDOM = quill.root;

      // 查找所有图片元素
      const imgElements = editorDOM.querySelectorAll('img');

      imgElements.forEach((img, index) => {
        console.log(`🖼️ 处理第${index + 1}张图片`);

        // 找到图片的最近父元素（不是p标签的）
        let parentElement = img.parentNode;
        let wrapperElement = null;

        // 向上查找，找到包裹图片的非p标签
        while (
          parentElement &&
          parentElement.tagName &&
          parentElement.tagName.toLowerCase() !== 'p'
        ) {
          wrapperElement = parentElement;
          parentElement = parentElement.parentNode;
        }

        // 如果图片被其他标签包裹（不是直接在p标签中）
        if (wrapperElement) {
          console.log(`🏷️ 图片被 ${wrapperElement.tagName} 标签包裹`);

          // 获取包裹标签的文本内容（除了图片）
          const fullText = wrapperElement.textContent.trim();
          console.log(`📝 包裹标签文本内容: "${fullText}"`);

          // 获取图片的属性
          const imgSrc = img.getAttribute('src');
          const imgFileId = img.getAttribute('data-file-id');
          const imgWidth = img.getAttribute('data-width');
          const imgHeight = img.getAttribute('data-height');

          console.log(
            `🖼️ 图片属性: src=${imgSrc?.substring(0, 50)}, fileId=${imgFileId}`
          );

          // 确保最外层是p标签
          let pElement = parentElement;
          if (!pElement || pElement.tagName.toLowerCase() !== 'p') {
            // 创建新的p标签
            pElement = document.createElement('p');
            if (parentElement) {
              parentElement.parentNode.replaceChild(pElement, parentElement);
            }
          }

          // 清空p标签内容
          pElement.innerHTML = '';

          // 重新创建图片元素，保持所有属性
          const newImg = document.createElement('img');
          newImg.setAttribute('src', imgSrc);
          if (imgFileId) newImg.setAttribute('data-file-id', imgFileId);
          if (imgWidth) newImg.setAttribute('data-width', imgWidth);
          if (imgHeight) newImg.setAttribute('data-height', imgHeight);

          // 添加图片到p标签
          pElement.appendChild(newImg);

          // 如果有文本内容，创建描述标签
          if (fullText) {
            const descElement = document.createElement('em');
            descElement.className = 'image-desc';
            descElement.setAttribute('data-image-desc', '1');
            descElement.textContent = fullText;
            pElement.appendChild(descElement);

            console.log(`✅ 已创建图片描述: "${fullText}"`);
          }

          console.log('✅ 图片结构已标准化为 <p><img>[描述]</p>');
        } else {
          console.log('✅ 图片已经在正确的结构中，无需处理');
        }
      });

      console.log('�� 嵌套图片提取完成');
    },
  },
};
</script>

<style lang="less" scoped>
.rich-text-editor {
  width: 100%;
  position: relative;
  .ql-toolbar {
    border-top: 1px solid #ccc;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
  }

  .ql-container {
    border-bottom: 1px solid #ccc;
    border-left: 1px solid #ccc;
    border-right: 1px solid #ccc;
    height: 500px !important; /* 使用 !important 强制应用高度 */

    .ql-editor {
      height: 470px !important; /* 编辑区域高度，减去工具栏的高度 */
      overflow-y: auto;
    }
  }
}

/* 为图片按钮添加 before 伪元素显示"插入图片"文案 */
/deep/ .ql-toolbar.ql-snow {
  height: 74px;
}

/deep/ .ql-snow .ql-toolbar button,
/deep/ .ql-snow.ql-toolbar button {
  position: relative;
  padding: 0;
  width: 64px;
  height: 54px;
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
}
/deep/ .ql-snow .ql-image {
  margin-right: 20px;
  &::before {
    content: '插入图片';
    position: absolute;
    top: 30px;
    left: 50%;
    transform: translateX(-50%);
    color: #333;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    text-align: center;
  }
  &:hover {
    .ql-stroke {
      stroke: #444;
    }
    .ql-fill {
      fill: #444;
    }
  }
  svg {
    float: none;
    margin: 0 auto;
    width: 25px;
    height: 25px;
    display: block;
    position: absolute;
    top: 5px;
    left: 50%;
    transform: translateX(-54%);
  }
}
/deep/ .ql-snow .ql-clean {
  margin-right: 10px;
  &::before {
    content: '清除样式';
    position: absolute;
    top: 30px;
    left: 50%;
    transform: translateX(-50%);
    color: #333;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    text-align: center;
  }
  &:hover {
    .ql-stroke {
      stroke: #444;
    }
    .ql-fill {
      fill: #444;
    }
  }
  svg {
    float: none;
    margin: 0 auto;
    width: 25px;
    height: 25px;
    display: block;
    position: absolute;
    top: 5px;
    left: 50%;
    transform: translateX(-54%);
  }
}
/deep/ .ql-snow .ql-artifact {
  &::before {
    content: '插入文物';
    position: absolute;
    top: 30px;
    left: 50%;
    transform: translateX(-50%);
    color: #333;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    text-align: center;
  }
  .icon {
    position: absolute;
    top: 2px;
    left: 50%;
    transform: translateX(-50%);
    display: inline-block;
    width: 30px;
    height: 30px;
    background: url('@/assets/images/wenwu-icon.png') no-repeat;
    background-size: 100% 100%;
  }
}

/* 自定义标题显示文本 - 只保留3项 */
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-label::before,
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-item::before {
  content: '正文';
  color: #333;
  font-weight: normal;
}
// /deep/ .ql-snow .ql-picker.ql-header .ql-picker-label[data-value='2']::before,
// /deep/ .ql-snow .ql-picker.ql-header .ql-picker-item[data-value='2']::before {
//   content: '标题1';
//   color: #333;
//   font-weight: bold;
// }
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-label[data-value='4']::before,
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-item[data-value='4']::before {
  content: '标题';
  color: #333;
  font-weight: bold;
}
/deep/
  .ql-snow
  .ql-picker.ql-header
  .ql-picker-label[data-value='false']::before,
/deep/
  .ql-snow
  .ql-picker.ql-header
  .ql-picker-item[data-value='false']::before {
  content: '正文';
  color: #333;
  font-weight: normal;
}

/deep/ .ql-snow .ql-picker.ql-header .ql-picker-label[data-value='1'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-item[data-value='1'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-label[data-value='2'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-item[data-value='2'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-label[data-value='3'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-item[data-value='3'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-label[data-value='5'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-item[data-value='5'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-label[data-value='6'],
/deep/ .ql-snow .ql-picker.ql-header .ql-picker-item[data-value='6'] {
  display: none;
}

.editor-tips {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 12px;
  color: #6c757d;

  span {
    margin-right: 10px;

    &:first-child {
      font-weight: bold;
    }
  }

  kbd {
    background: #f1f3f4;
    border: 1px solid #dadce0;
    border-radius: 3px;
    padding: 2px 4px;
    font-size: 11px;
    font-family: monospace;
  }
}

.artifact-search {
  margin-bottom: 20px;
}

.artifact-list {
  max-height: 400px;
  overflow-y: auto;

  .artifact-item {
    display: flex;
    padding: 10px;
    border: 1px solid #e0e0e0;
    margin-bottom: 10px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background-color: #f5f5f5;
    }

    img {
      width: 80px;
      height: 80px;
      object-fit: cover;
      margin-right: 15px;
      border-radius: 4px;
    }

    .artifact-info {
      flex: 1;

      h4 {
        margin: 0 0 8px 0;
        font-size: 16px;
        color: #333;
      }

      p {
        margin: 0;
        font-size: 14px;
        color: #666;
        line-height: 1.4;
      }
    }
  }
}

/deep/ .artifact-link {
  color: #04cbcd;
  text-decoration: none;
  padding: 2px 6px;
  background-color: #f0f9ff;
  border-radius: 3px;

  &:hover {
    background-color: #e0f4ff;
  }
}

/deep/ .ql-snow .ql-editor img {
  width: 300px;
}
/deep/ .ql-snow .ql-editor img[data-artifact-id] {
  border: 2px solid #04cbcd !important;
  border-radius: 4px !important;
  padding: 2px !important;
  cursor: pointer;
}
/deep/ .artifact-desc {
  display: block;
  color: #333;
  opacity: 0.6;
  font-style: italic;
}
/deep/ .image-desc {
  display: block;
  color: #333;
  opacity: 0.6;
  font-style: italic;
}

/* 字符计数样式 */
.editor-char-count {
  text-align: right;
  margin-top: 8px;
  font-size: 12px;
  color: #666;
  position: absolute;
  right: 10px;
  bottom: 50px;

  .char-limit-min {
    color: #04cbcd;
  }

  .char-limit-warning {
    color: #f56c6c;
  }

  .char-limit-exceeded {
    color: #f56c6c;
    font-weight: bold;
  }

  .min-char-tip {
    margin-left: 10px;
    color: #04cbcd;
    font-style: italic;
  }

  .max-char-tip {
    margin-left: 10px;
    color: #f56c6c;
    font-weight: bold;
  }
}

/* 粘贴处理状态提示 */
.paste-processing {
  position: absolute;
  top: 50px; /* 调整位置，使其不与工具栏重叠 */
  right: 10px;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;

  .el-icon-loading {
    margin-right: 8px;
    font-size: 18px;
    color: #04cbcd;
  }
}
</style>
